/**
 * The share/export control.
 *
 * Renders NOTHING when the `share` flag is off — not a disabled button, not a
 * "turn this on" prompt. A feature the user has switched off should be absent,
 * not advertised on every screen.
 *
 * The outcome line matters: `navigator.share` silently does nothing visible on
 * a desktop browser, so without feedback the button looks broken. "Copied" is
 * as much of a success as "shared".
 */
import { useState } from "react";

import { useApp } from "../state.js";
import { downloadCsv, shareText } from "../share.js";
import type { ShareResult } from "../share.js";

export type ShareAction = {
  label: string;
  /** built lazily — no message is composed until the user asks for one */
  build: () => { title: string; body: string };
};

export type CsvAction = {
  label: string;
  filename: string;
  build: () => Array<Array<string | number>>;
};

const OUTCOME: Record<ShareResult, string> = {
  shared: "Shared.",
  copied: "Copied to the clipboard.",
  cancelled: "",
  unavailable: "This browser would not share or copy. Use the CSV instead.",
};

export function ShareBar({
  actions = [],
  csv = [],
}: {
  actions?: ShareAction[];
  csv?: CsvAction[];
}) {
  const { flags } = useApp();
  const [note, setNote] = useState("");

  if (!flags.share) return null;
  if (actions.length === 0 && csv.length === 0) return null;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {actions.map((action) => (
          <button
            key={action.label}
            type="button"
            className={chipClass}
            onClick={() => {
              const { title, body } = action.build();
              void shareText(title, body).then((result) => setNote(OUTCOME[result]));
            }}
          >
            {action.label}
          </button>
        ))}

        {csv.map((action) => (
          <button
            key={action.label}
            type="button"
            className={chipClass}
            onClick={() => {
              downloadCsv(action.filename, action.build());
              setNote("Downloaded.");
            }}
          >
            {action.label}
          </button>
        ))}
      </div>

      {note && <p className="px-1 text-xs text-ink-3">{note}</p>}
    </div>
  );
}

const chipClass =
  "rounded-full border border-line bg-surface px-3.5 py-2 text-[13px] font-medium text-ink-2 " +
  "transition-transform active:scale-[0.97]";
